import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn, getTestId } from '../../utils';
import { Icon } from '../icons/Icon';

export type FilterChipTriggerVariant = 'set' | 'add';

export interface FilterChipProps {
  /** `FilterChip.Trigger` and `FilterChip.Remove` */
  children: React.ReactNode;
  className?: string;
  testId?: string;
  id?: string;
}

const FilterChipRoot = React.forwardRef<HTMLSpanElement, FilterChipProps>(
  ({ children, className, testId, id }, ref) => (
    <span
      ref={ref}
      id={id}
      data-testid={getTestId({ testId, id })}
      className={cn(
        'inline-flex items-center gap-2xs h-7.5 rounded-full whitespace-nowrap text-sm',
        'bg-background-input border border-border-input',
        className
      )}
    >
      {children}
    </span>
  )
);

FilterChipRoot.displayName = 'FilterChip';

const triggerVariants = cva(
  [
    'inline-flex items-center gap-xs whitespace-nowrap text-sm rounded-full cursor-pointer transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-base'
  ],
  {
    variants: {
      variant: {
        set: 'h-full pl-sm pr-xs text-foreground-base',
        add: 'h-7.5 px-sm bg-background-input border border-dashed border-border-input text-foreground-muted hover:bg-background-muted'
      }
    },
    defaultVariants: { variant: 'set' }
  }
);

type BaseTriggerAttributes = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'value' | 'type'
>;

export interface FilterChipTriggerProps extends BaseTriggerAttributes {
  /** Parameter name, muted in front of the value; the text after the plus of an `add` chip */
  label: string;
  /** Chosen value shown after the label (`set` only) */
  value?: string;
  /** `set` sits inside `FilterChip`; `add` is a standalone dashed chip with a plus */
  variant?: FilterChipTriggerVariant;
  /** Caret after the value (`set` only), for a chip that expands a panel */
  caret?: 'down' | 'up';
  testId?: string;
}

const FilterChipTrigger = React.forwardRef<HTMLButtonElement, FilterChipTriggerProps>(
  ({ label, value, variant = 'set', caret, testId, id, className, ...props }, ref) => (
    <button
      ref={ref}
      type='button'
      id={id}
      data-testid={getTestId({ testId, id })}
      className={cn(triggerVariants({ variant }), className)}
      {...props}
    >
      {variant === 'set' ? (
        <>
          <span className='font-medium text-foreground-muted'>{label}</span>
          {value && <span>{value}</span>}
          {caret && (
            <Icon
              name='chevron-down'
              rotate={caret === 'up' ? 180 : undefined}
              className='w-3! h-3! text-foreground-muted'
              aria-hidden='true'
            />
          )}
        </>
      ) : (
        <>
          <Icon name='plus' className='w-3! h-3!' aria-hidden='true' />
          {label}
        </>
      )}
    </button>
  )
);

FilterChipTrigger.displayName = 'FilterChip.Trigger';

export interface FilterChipRemoveProps {
  onClick: () => void;
  /** Accessible name of the icon button */
  label: string;
  testId?: string;
}

const FilterChipRemove = React.forwardRef<HTMLButtonElement, FilterChipRemoveProps>(
  ({ onClick, label, testId }, ref) => (
    <button
      ref={ref}
      type='button'
      aria-label={label}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center size-5 mr-xs rounded-full cursor-pointer',
        'text-foreground-muted hover:bg-background-muted transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-base'
      )}
    >
      <Icon name='xmark' className='w-3! h-3!' aria-hidden='true' />
    </button>
  )
);

FilterChipRemove.displayName = 'FilterChip.Remove';

/**
 * A rounded filter chip of a horizontal filter bar: `Parametr hodnota ✕`.
 * The trigger works as a Headless UI `PopoverButton` child.
 */
export const FilterChip = Object.assign(FilterChipRoot, {
  Trigger: FilterChipTrigger,
  Remove: FilterChipRemove
});
